import React, { useEffect, useState, useRef } from 'react';
import cn from 'classnames';
import Image from 'next/image';

import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';

import { Section } from 'react-scale/components';

import { customersQuotes } from './quotes';

const Quote = ({ data, quotes, image, bg, gradientText = true, paddingTop, paddingBottom, container }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideCount, setSlideCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [pause, setPause] = useState(false);
  const timer = useRef();

  const [sliderRef, slider] = useKeenSlider({
    loop: true,
    created(s) {
      setLoaded(true);
      setSlideCount(s.slides.length);
      s.container.addEventListener('mouseover', () => {
        setPause(true);
      });
      s.container.addEventListener('mouseout', () => {
        setPause(false);
      });
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel);
    },
    dragStarted: () => {
      setPause(true);
    },
    dragEnded: () => {
      setPause(false);
    }
  });

  useEffect(() => {
    if (slideCount > 1) {
      timer.current = setInterval(() => {
        if (!pause && slider) {
          slider.current?.next();
        }
      }, 5000);
      return () => {
        clearInterval(timer.current);
      };
    }
  }, [pause, slider]);

  return (
    <Section container={container} relative paddingTop={paddingTop} paddingBottom={paddingBottom}>
      <div className="z-10 space-y-10 sm:space-y-16">
        <div className="relative overflow-hidden bg-gray-900 rounded-xl">
          <div ref={sliderRef} className="keen-slider">
            {data
              ? data?.map(item => (
                  <div
                    className="flex flex-col gap-8 keen-slider__slide md:flex-row md:flex-nowrap md:items-center"
                    key={item.id}
                  >
                    <BlockQuote
                      quote={{
                        content: item.quoteAuthorQuote,
                        profile: item.quoteAuthorName,
                        position: item.quoteAuthorPosition,
                        logo: {
                          src: item.quoteLogo.url,
                          alt: item.quoteLogo.alt,
                          title: item.quoteLogo.title,
                          width: item.quoteLogo.width,
                          height: item.quoteLogo.height
                        }
                      }}
                      image={image}
                      gradientText={gradientText}
                    />
                  </div>
                ))
              : quotes?.map(q => {
                  const quote = typeof q === 'string' ? q : q.quote;

                  return (
                    <div
                      className="flex flex-col gap-8 keen-slider__slide lg:flex-row lg:flex-nowrap lg:items-center"
                      key={customersQuotes[quote].logo.alt}
                    >
                      <BlockQuote
                        quote={customersQuotes[quote]}
                        image={image}
                        gradientText={gradientText}
                      />
                    </div>
                  );
                })}
          </div>
          {!image && <div className="bg-gradient-x absolute bottom-0 h-[4px] w-full" />}
        </div>
        {loaded && slideCount > 1 && (
          <div className="mx-auto flex items-center justify-center sm:max-w-max sm:space-x-[72px]">
            <div
              className={cn(
                bg ?? 'bg-gray-800',
                'hidden h-10 w-10 rotate-180 cursor-pointer select-none items-center justify-center rounded-full sm:flex'
              )}
              onClick={e => e.stopPropagation() || slider.current?.prev()}
            >
              <span className="text-xl">{`\u2192`}</span>
            </div>
            {slider && (
              <div className="flex justify-center space-x-2 transform bottom-4 max-w-max">
                {[...Array(slider.current.slides.length).keys()].map(idx => (
                  <span
                    key={idx}
                    onClick={() => slider.current?.moveToIdx(idx)}
                    className={cn(
                      'h-[10px] w-[10px] cursor-pointer rounded-full',
                      currentSlide === idx ? 'bg-white' : 'bg-[#37383B]'
                    )}
                  />
                ))}
              </div>
            )}
            <div
              className={cn(
                bg ?? 'bg-gray-800',
                'hidden h-10 w-10 cursor-pointer select-none items-center justify-center rounded-full sm:flex'
              )}
              onClick={e => e.stopPropagation() || slider.current?.next()}
            >
              <span className="text-xl">{`\u2192`}</span>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
};

const BlockQuote = ({ quote, image, gradientText }) => (
  <>
    {!image && (
      <div className="box-content flex w-4/12 p-8 pb-0 lg:justify-center lg:p-0">
        <Image
          loading="eager"
          width={quote.logo.width}
          quality={100}
          height={quote.logo.height}
          src={quote.logo.src}
        />
      </div>
    )}
    {image && (
      <div className="relative h-52 lg:h-full lg:w-6/12">
        <Image layout="fill" quality={100} src={quote.img.src} objectFit="cover" />
      </div>
    )}
    <div className={cn('w-full p-8 lg:p-20 lg:pl-0', image ? 'lg:w-6/12 lg:pl-8' : 'lg:w-8/12')}>
      {image && (
        <div className="flex h-16 mb-8 max-w-4/12 lg:h-8">
          <Image
            width={quote.logo.width}
            quality={100}
            height={quote.logo.height}
            src={quote.logo.src}
          />
        </div>
      )}
      <blockquote
        className={cn('mb-8 text-base sm:text-lg lg:leading-relaxed text-white', {
          'lg:text-2xl': !image
        })}
      >
        {quote.content}
      </blockquote>
      <h4
        className={cn(
          'mb-4 inline-block text-lg leading-snug sm:mb-0',
          { 'lg:text-2xl': !image },
          gradientText ? 'text-transform-gradient font-aeonik sm:text-xl' : 'font-bold'
        )}
      >
        {quote.profile}
      </h4>
      <p
        className={cn('mt-2 font-aeonik text-base text-gray-300 sm:text-lg', {
          'lg:text-xl': !image
        })}
      >
        {quote.position}
      </p>
    </div>
  </>
);

Quote.defaultProps = {
  quotes: ['toyota', 'nuro'],
  image: false,
  bg: 'bg-gray-800',
  paddingTop: false,
  paddingBottom: false,
  container: false
};

export default Quote;
